import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, ShieldCheck, Warning, Bell, MagnifyingGlass, ClockCounterClockwise, CaretDown, PencilSimple, FilePdf, NewspaperClipping, ArrowSquareOut } from '@phosphor-icons/react';
import { formatLabel, STATUS_STYLES, RISK_STYLES, ALERT_STATUS_STYLES, HIT_STATUS_STYLES, timeAgo, formatFieldName, BADGE } from '@/lib/format';
import ResolutionModal from '@/components/ResolutionModal';
import CaseProgressTracker from '@/components/CaseProgressTracker';
import MatchDetailDialog from '@/components/MatchDetailDialog';
import ReportPreviewModal from '@/components/ReportPreviewModal';
import { HelpTooltip } from '@/components/HelpTooltip';
import { displayRelation } from '@/lib/relations';
import BulkActionBar from '@/components/BulkActionBar';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [caseData, setCaseData] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [mediaFilter, setMediaFilter] = useState('pending'); // all | pending | flagged | dismissed
  const [screening, setScreening] = useState(false);
  const [riskCalc, setRiskCalc] = useState(false);
  const [riskResult, setRiskResult] = useState(null);
  const [audits, setAudits] = useState([]);
  const [transComment, setTransComment] = useState('');
  // Resolution modal state
  const [resModal, setResModal] = useState({ open: false, type: null, id: null, resolution: null, matchName: null, matchData: null });
  // Match detail (read-only) dialog state — for auto-resolved / resolved / weak rows
  const [matchDetail, setMatchDetail] = useState({ open: false, alert: null });
  // Report preview state
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  // Media alerts state
  const [mediaAlerts, setMediaAlerts] = useState([]);
  const [mediaFeedUrl, setMediaFeedUrl] = useState('');
  const [savingFeed, setSavingFeed] = useState(false);
  const [checkingMedia, setCheckingMedia] = useState(false);
  // 3A: Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  // 3E: Case navigation
  const [caseList, setCaseList] = useState([]);

  const fetchCase = async () => {
    try {
      const [c, w] = await Promise.all([api.get(`/cases/${id}`), api.get(`/cases/${id}/workflow`)]);
      setCaseData(c.data);
      setWorkflow(w.data);
    } catch (e) { }
  };

  const fetchAudit = async () => {
    try { const r = await api.get(`/audit/case/${id}`); setAudits(r.data.logs); } catch (e) { }
  };

  useEffect(() => { fetchCase(); }, [id]);
  useEffect(() => { if (caseData?.full_name) document.title = `Radar — ${caseData.full_name}`; }, [caseData]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab]);
  useEffect(() => {
    if (tab === 'media') {
      api.get(`/media-alerts/case/${id}`).then(r => setMediaAlerts(r.data.alerts || [])).catch(() => {});
    }
  }, [tab, id]);
  // Sync tab → URL so Active Feeds link (/cases/:id?tab=media) opens straight into Media
  useEffect(() => {
    const current = searchParams.get('tab');
    if (tab === 'overview') {
      if (current) { searchParams.delete('tab'); setSearchParams(searchParams, { replace: true }); }
    } else if (current !== tab) {
      searchParams.set('tab', tab);
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => { api.get('/cases', { params: { limit: 200 } }).then(r => setCaseList(r.data.cases.map(c => c.id))).catch(() => {}); }, []);

  // ─── Bulk selection state (Case Detail) ────────────────────────
  // Two independent selection sets — one per list, so an analyst's selection
  // on the Screening tab doesn't spill onto the Monitoring tab (or vice versa).
  // Declared before any early-return so React Hooks rules-of-hooks are honored.
  const [selectedAlertIds, setSelectedAlertIds] = useState(new Set());
  const [selectedHitIds, setSelectedHitIds] = useState(new Set());

  if (!caseData) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>;

  // 3E: Prev/Next
  const curIdx = caseList.indexOf(id);
  const prevId = curIdx > 0 ? caseList[curIdx - 1] : null;
  const nextId = curIdx < caseList.length - 1 ? caseList[curIdx + 1] : null;

  // 3A: Edit handlers
  const startEdit = () => { setEditForm({ full_name: caseData.full_name || '', nationality: caseData.nationality || '', country_of_residence: caseData.country_of_residence || '', date_of_birth: caseData.date_of_birth || '', id_type: caseData.id_type || '', id_number: caseData.id_number || '', occupation: caseData.occupation || '', source_of_wealth: caseData.source_of_wealth || '', notes: caseData.notes || '' }); setEditing(true); };
  const saveEdit = async () => { try { await api.put(`/cases/${id}`, editForm); setEditing(false); await fetchCase(); } catch (e) { alert(e.response?.data?.detail || 'Save failed'); } };

  const handleScreen = async () => {
    setScreening(true);
    try { await api.post(`/cases/${id}/screen`); await fetchCase(); } catch (e) { alert('Screening failed'); }
    setScreening(false);
  };

  const handleRisk = async () => {
    setRiskCalc(true);
    try { const r = await api.post(`/risk/calculate/${id}`); setRiskResult(r.data); await fetchCase(); } catch (e) { alert('Risk calculation failed'); }
    setRiskCalc(false);
  };

  const handleMonitoringToggle = async () => {
    try { await api.put(`/cases/${id}/monitoring`, { monitoring_enabled: !caseData.monitoring_enabled }); await fetchCase(); } catch (e) { alert('Failed to toggle monitoring'); }
  };

  const handleTransition = async (newStatus) => {
    try { await api.post(`/cases/${id}/transition`, { new_status: newStatus, comment: transComment || null }); setTransComment(''); await fetchCase(); } catch (e) { alert(e.response?.data?.detail || 'Transition failed'); }
  };

  const handleResolveHit = async (hitId, status) => {
    const hit = caseData.monitoring_hits?.find(h => h.id === hitId);
    setResModal({ open: true, type: 'hit', id: hitId, resolution: status, matchName: hit?.matched_entry_name, matchData: hit });
  };

  const handleResolveAlert = async (alertId, status) => {
    const alert = caseData.match_alerts?.find(a => a.id === alertId);
    setResModal({ open: true, type: 'alert', id: alertId, resolution: status, matchName: alert?.matched_entry_name, matchData: alert?.matched_entry_data });
  };

  const confirmResolution = async (comment) => {
    try {
      if (resModal.type === 'alert') {
        await api.put(`/match-alerts/${resModal.id}/resolve`, { status: resModal.resolution, comment });
      } else if (resModal.type === 'hit') {
        await api.put(`/monitoring/hits/${resModal.id}/resolve`, { status: resModal.resolution, comment });
      } else if (resModal.type === 'media') {
        await api.put(`/media-alerts/${resModal.id}/resolve`, { status: resModal.resolution, comment });
        const r = await api.get(`/media-alerts/case/${id}`); setMediaAlerts(r.data.alerts || []);
      }
      setResModal({ open: false, type: null, id: null, resolution: null, matchName: null, matchData: null });
      await fetchCase();
    } catch (e) { alert(e.response?.data?.detail || 'Resolution failed'); }
  };

  // ─── Bulk selection state (Case Detail) ────────────────────────
  // (state declared above, before the early return, to honor Hooks rules.)
  const toggleAlertSelect = (aid) => {
    setSelectedAlertIds(prev => {
      const n = new Set(prev);
      n.has(aid) ? n.delete(aid) : n.add(aid);
      return n;
    });
  };
  const toggleHitSelect = (hid) => {
    setSelectedHitIds(prev => {
      const n = new Set(prev);
      n.has(hid) ? n.delete(hid) : n.add(hid);
      return n;
    });
  };
  const clearAlertSelection = () => setSelectedAlertIds(new Set());
  const clearHitSelection = () => setSelectedHitIds(new Set());

  // Bulk action for match alerts — loops the SAME endpoint the single-row
  // buttons call, so whitelist/escalation side-effects fire per row identically.
  const runBulkMatchAlertsAction = async (status, comment) => {
    const ids = Array.from(selectedAlertIds);
    const results = await Promise.allSettled(
      ids.map(aid => api.put(`/match-alerts/${aid}/resolve`, { status, comment: comment || `Bulk ${status}` }))
    );
    let ok = 0, failed = 0;
    const failedIds = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok += 1;
      else { failed += 1; failedIds.push(ids[i]); }
    });
    if (failed > 0) console.warn('[Case Detail match_alerts bulk] failures:', failedIds);
    await fetchCase();
    setSelectedAlertIds(new Set(failedIds));
    return { ok, failed };
  };

  // Bulk action for monitoring hits — loops the SAME endpoint the single-row
  // buttons call.
  const runBulkMonitoringHitsAction = async (status, comment) => {
    const ids = Array.from(selectedHitIds);
    const results = await Promise.allSettled(
      ids.map(hid => api.put(`/monitoring/hits/${hid}/resolve`, { status, comment: comment || `Bulk ${status}` }))
    );
    let ok = 0, failed = 0;
    const failedIds = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok += 1;
      else { failed += 1; failedIds.push(ids[i]); }
    });
    if (failed > 0) console.warn('[Case Detail monitoring_hits bulk] failures:', failedIds);
    await fetchCase();
    setSelectedHitIds(new Set(failedIds));
    return { ok, failed };
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'screening', label: 'Screening' },
    { id: 'risk', label: 'Risk Assessment' },
    { id: 'monitoring', label: 'Monitoring' },
    { id: 'media', label: 'Media' },
    { id: 'audit', label: 'Audit Trail' },
  ];

  return (
    <div data-testid="case-detail-page">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate('/cases')} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0052FF] transition-all">
          <ArrowLeft size={16} /> Back to Cases
        </button>
        {/* 3E: Prev/Next Navigation */}
        <div className="flex gap-2">
          {prevId && <button onClick={() => navigate(`/cases/${prevId}`)} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">← Previous Case</button>}
          {nextId && <button onClick={() => navigate(`/cases/${nextId}`)} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Next Case →</button>}
        </div>
      </div>

      {/* Feature 20: Workflow Progress Tracker */}
      <CaseProgressTracker caseData={caseData} onTabChange={setTab} onAction={(stepId) => { if (stepId === 'screened') handleScreen(); }} />

      {/* Header */}
      <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold text-[#0A0A0A]">{caseData.full_name}</h1>
            {caseData.account_name && (
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className="text-[#52525B]">Account:</span>
                <button onClick={() => navigate(`/accounts/${caseData.account_id_ref}`)} data-testid="case-account-link" className="text-[#0052FF] hover:underline font-medium">{caseData.account_name}</button>
                {caseData.account_code && <span className="font-mono text-[#A1A1AA]">({caseData.account_code})</span>}
                {caseData.account_relation && <span className={`${BADGE} bg-teal-50 text-teal-700 border-teal-200`}>{caseData.account_relation}</span>}
              </div>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs uppercase tracking-wider text-[#52525B]">{formatLabel(caseData.case_type)}</span>
              <span className={STATUS_STYLES[caseData.status] || ''}>{formatLabel(caseData.status)}</span>
              <span className={RISK_STYLES[caseData.risk_rating] || ''}>{caseData.risk_rating} ({caseData.risk_score})</span>
              {caseData.monitoring_enabled && <span className={`${BADGE} bg-blue-50 text-blue-700 border-blue-200`}>Monitored</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleScreen} disabled={screening} className="px-3 py-1.5 text-xs font-medium bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50">
              <MagnifyingGlass size={14} className="inline mr-1" />{screening ? 'Screening...' : 'Run Screening'}
            </button>
            <button onClick={handleRisk} disabled={riskCalc} className="px-3 py-1.5 text-xs font-medium border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] disabled:opacity-50">
              <Warning size={14} className="inline mr-1" />{riskCalc ? 'Calculating...' : 'Assess Risk'}
            </button>
            <button onClick={handleMonitoringToggle} className={`px-3 py-1.5 text-xs font-medium rounded ${caseData.monitoring_enabled ? 'bg-green-50 text-green-700 border border-green-200' : 'border border-[#E4E4E7] text-[#52525B] hover:bg-[#F0F0F1]'}`}>
              <Bell size={14} className="inline mr-1" />{caseData.monitoring_enabled ? 'Monitoring ON' : 'Enable Monitoring'}
            </button>
            <button onClick={() => setReportPreviewOpen(true)} data-testid="preview-case-report-btn" className="px-3 py-1.5 text-xs font-medium border border-[#E4E4E7] rounded hover:bg-[#F0F0F1]">
              <FilePdf size={14} className="inline mr-1" />Report
            </button>
          </div>
        </div>

        {/* Status Transitions */}
        {workflow?.allowed_transitions?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#52525B]">Transition to:</span>
              <input value={transComment} onChange={e => setTransComment(e.target.value)} placeholder="Comment (optional)" className="px-2 py-1 text-xs border border-[#E4E4E7] rounded flex-1 max-w-xs" />
              {workflow.allowed_transitions.map(t => (
                <button key={t} onClick={() => handleTransition(t)} className="px-2 py-1 text-xs border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] capitalize">{t.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E4E4E7]">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>{t.label}</button>
        ))}
      </div>

      {/* OVERVIEW TAB — 3A: Editable */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-[#52525B]">Case Details</h3>
              {!editing ? (
                <button onClick={startEdit} className="flex items-center gap-1 px-2.5 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]"><PencilSimple size={12} /> Edit Case</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setEditing(false)} className="px-2.5 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
                  <button onClick={saveEdit} className="px-2.5 py-1 text-xs bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Save Changes</button>
                </div>
              )}
            </div>
            {!editing ? (
              <div className="space-y-2">
                {[['Full Name', caseData.full_name], ['Type', formatLabel(caseData.case_type)], ['Account', caseData.account_name ? `${caseData.account_name}${caseData.account_code ? ' (' + caseData.account_code + ')' : ''}` : null], ['Relation', displayRelation(caseData.account_relation)], ['Nationality', caseData.nationality], ['Residence', caseData.country_of_residence], ['DOB', caseData.date_of_birth], ['ID', `${caseData.id_type || ''} ${caseData.id_number || ''}`], ['Source of Wealth', caseData.source_of_wealth], ['Source', caseData.source], ['Created', caseData.created_at ? new Date(caseData.created_at).toLocaleString() : ''], ['Created By', caseData.created_by]].map(([k, v]) => v ? (
                  <div key={k} className="flex justify-between text-sm"><span className="text-[#52525B]">{k}</span><span className="font-medium text-[#0A0A0A]">{v}</span></div>
                ) : null)}
              </div>
            ) : (
              <div className="space-y-2">
                {[['full_name', 'Full Name', 'text'], ['nationality', 'Nationality', 'text'], ['country_of_residence', 'Country of Residence', 'text'], ['date_of_birth', 'Date of Birth', 'date'], ['id_type', 'ID Type', 'text'], ['id_number', 'ID Number', 'text'], ['occupation', 'Occupation', 'text'], ['source_of_wealth', 'Source of Wealth', 'text']].map(([key, label, type]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-[#52525B] w-32 shrink-0">{label}</span>
                    <input type={type} value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))} className="flex-1 px-2 py-1 text-sm border border-[#E4E4E7] rounded-md" />
                  </div>
                ))}
                <div className="flex items-start gap-3">
                  <span className="text-xs text-[#52525B] w-32 shrink-0 mt-1">Notes</span>
                  <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="flex-1 px-2 py-1 text-sm border border-[#E4E4E7] rounded-md" />
                </div>
              </div>
            )}
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-[#52525B] mb-3">Risk & Monitoring</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-[#52525B]">Risk Rating</span><span className={RISK_STYLES[caseData.risk_rating] || ''}>{caseData.risk_rating}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#52525B]">Risk Score</span><span className="font-medium">{caseData.risk_score}/100</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#52525B]">Monitoring</span><span className={`text-xs font-medium ${caseData.monitoring_enabled ? 'text-green-600' : 'text-gray-400'}`}>{caseData.monitoring_enabled ? 'Active' : 'Disabled'}</span></div>
              {caseData.last_monitored_at && <div className="flex justify-between text-sm"><span className="text-[#52525B]">Last Monitored</span><span className="text-xs">{new Date(caseData.last_monitored_at).toLocaleString()}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-[#52525B]">Screenings</span><span className="font-medium">{caseData.screening_history?.length || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#52525B]">Monitoring Hits</span><span className="font-medium">{caseData.monitoring_hits?.length || 0}</span></div>
            </div>
            {caseData.notes && <div className="mt-4 pt-3 border-t border-[#E4E4E7]"><p className="text-xs text-[#52525B]">Notes</p><p className="text-sm mt-1">{caseData.notes}</p></div>}
          </div>
        </div>
      )}

      {/* SCREENING TAB — 2A: Match Alert Comparison Cards + 2C: Enhanced Display */}
      {tab === 'screening' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-base font-semibold">Screening & Match Alerts</h3>
              {caseData.match_alerts?.filter(a => a.status === 'pending').length > 0 && (
                <p className="text-xs text-red-600 mt-0.5">{caseData.match_alerts.filter(a => a.status === 'pending').length} unresolved alert(s) — must resolve before clearing case</p>
              )}
            </div>
            <button onClick={handleScreen} disabled={screening} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50">{screening ? 'Running...' : 'Run New Screening'}</button>
          </div>

          {/* TIERED Match Alerts */}
          {(() => {
            const alerts = caseData.match_alerts || [];
            const strong = alerts.filter(a => a.match_score >= 85 && a.status === 'pending');
            const potential = alerts.filter(a => a.match_score >= 60 && a.match_score < 85 && a.status === 'pending');
            const weak = alerts.filter(a => a.match_score < 60 && a.status === 'pending');
            const autoRes = alerts.filter(a => a.status === 'auto_dismissed' || a.status === 'whitelisted');
            const resolved = alerts.filter(a => ['true_positive','false_positive','inconclusive'].includes(a.status));
            const pendingAll = [...strong, ...potential, ...weak];
            const allPendingSelected = pendingAll.length > 0 && pendingAll.every(a => selectedAlertIds.has(a.id));
            const toggleSelectAllPending = () => {
              if (allPendingSelected) setSelectedAlertIds(new Set());
              else setSelectedAlertIds(new Set(pendingAll.map(a => a.id)));
            };
            return alerts.length > 0 ? (
              <div className="mb-6">
                {/* Tier Summary Bar */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-white border border-[#E4E4E7] rounded-md">
                  <span className={`${BADGE} bg-red-100 text-red-700 border-red-300`}>🔴 {strong.length} Strong</span>
                  <span className={`${BADGE} bg-amber-100 text-amber-700 border-amber-300`}>🟠 {potential.length} Potential</span>
                  <span className={`${BADGE} bg-gray-100 text-gray-600 border-gray-300`}>⚪ {weak.length} Weak</span>
                  {autoRes.length > 0 && <span className="text-xs text-[#A1A1AA]">({autoRes.length} auto-resolved)</span>}
                  {resolved.length > 0 && <span className="text-xs text-green-600">({resolved.length} resolved)</span>}
                  <span className="text-xs text-[#52525B] ml-auto">Total: {alerts.length}</span>
                </div>
                {/* Bulk select-all row */}
                {pendingAll.length > 0 && (
                  <div className="flex items-center gap-2 mb-2 text-xs text-[#52525B]" data-testid="alert-bulk-select-all-row">
                    <input
                      type="checkbox"
                      checked={allPendingSelected}
                      onChange={toggleSelectAllPending}
                      className="w-4 h-4 cursor-pointer accent-[#0052FF]"
                      data-testid="alert-select-all"
                      aria-label="Select all pending match alerts"
                    />
                    <label className="cursor-pointer" onClick={toggleSelectAllPending}>
                      {allPendingSelected ? 'Deselect' : 'Select'} all {pendingAll.length} pending match alert{pendingAll.length === 1 ? '' : 's'}
                    </label>
                  </div>
                )}
                {/* STRONG MATCHES */}
                {strong.length > 0 && (
                  <div className="mb-4">
                    <div className="py-1 mb-2 border-b-2 border-red-400"><span className="text-xs font-bold text-red-700 uppercase tracking-wider">Strong Matches ({strong.length}) — Requires Immediate Review</span></div>
                    <div className="space-y-3">{strong.map(a => <div key={a.id} className="flex items-start gap-2"><input type="checkbox" checked={selectedAlertIds.has(a.id)} onChange={() => toggleAlertSelect(a.id)} className="mt-3 w-4 h-4 cursor-pointer accent-[#0052FF]" data-testid={`alert-checkbox-${a.id}`} aria-label={`Select alert ${a.matched_entry_name}`} /><div className="flex-1"><MatchAlertCard alert={a} caseData={caseData} onResolve={handleResolveAlert} /></div></div>)}</div>
                  </div>
                )}
                {/* POTENTIAL MATCHES */}
                {potential.length > 0 && (
                  <div className="mb-4">
                    <div className="py-1 mb-2 border-b-2 border-amber-400"><span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Potential Matches ({potential.length}) — Review Recommended</span></div>
                    <div className="space-y-3">{potential.map(a => <div key={a.id} className="flex items-start gap-2"><input type="checkbox" checked={selectedAlertIds.has(a.id)} onChange={() => toggleAlertSelect(a.id)} className="mt-3 w-4 h-4 cursor-pointer accent-[#0052FF]" data-testid={`alert-checkbox-${a.id}`} aria-label={`Select alert ${a.matched_entry_name}`} /><div className="flex-1"><MatchAlertCard alert={a} caseData={caseData} onResolve={handleResolveAlert} /></div></div>)}</div>
                  </div>
                )}
                {/* WEAK MATCHES — Collapsed */}
                <WeakMatchesSection alerts={weak} caseData={caseData} onResolve={handleResolveAlert} onView={(a) => setMatchDetail({ open: true, alert: a })} selectedIds={selectedAlertIds} onToggleSelect={toggleAlertSelect} />
                {/* AUTO-RESOLVED */}
                {autoRes.length > 0 && (
                  <details className="mb-4"><summary className="text-xs text-[#A1A1AA] cursor-pointer hover:text-[#52525B] py-2">▶ {autoRes.length} auto-resolved (click a row to view details)</summary>
                    <div className="mt-2 bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
                      <table className="w-full"><thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]"><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Name</th><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Score</th><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Reason</th><th className="px-3 py-1.5"></th></tr></thead>
                        <tbody>{autoRes.map(a => <tr key={a.id} onClick={() => setMatchDetail({ open: true, alert: a })} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] cursor-pointer" data-testid={`auto-resolved-row-${a.id}`}><td className="px-3 py-1.5 text-xs">{a.matched_entry_name||'N/A'}</td><td className="px-3 py-1.5 text-xs">{a.match_score?.toFixed(1)}%</td><td className="px-3 py-1.5 text-xs text-[#A1A1AA]">{a.resolution_comment||formatLabel(a.status)}</td><td className="px-3 py-1.5 text-xs text-[#0052FF]">View →</td></tr>)}</tbody></table></div></details>
                )}
                {/* RESOLVED */}
                {resolved.length > 0 && (
                  <details className="mb-4"><summary className="text-xs text-green-600 cursor-pointer py-2">▶ {resolved.length} manually resolved (click a row to view remark &amp; details)</summary>
                    <div className="mt-2 bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
                      <table className="w-full"><thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]"><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Name</th><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Score</th><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">Resolution</th><th className="text-left px-3 py-1.5 text-xs text-[#52525B]">By</th><th className="px-3 py-1.5"></th></tr></thead>
                        <tbody>{resolved.map(a => <tr key={a.id} onClick={() => setMatchDetail({ open: true, alert: a })} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] cursor-pointer" data-testid={`resolved-row-${a.id}`}><td className="px-3 py-1.5 text-xs">{a.matched_entry_name||'N/A'}</td><td className="px-3 py-1.5 text-xs">{a.match_score?.toFixed(1)}%</td><td className="px-3 py-1.5"><span className={ALERT_STATUS_STYLES[a.status]||''}>{formatLabel(a.status)}</span></td><td className="px-3 py-1.5 text-xs text-[#A1A1AA]">{a.resolved_by||''}</td><td className="px-3 py-1.5 text-xs text-[#0052FF]">View →</td></tr>)}</tbody></table></div></details>
                )}
              </div>
            ) : <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA] mb-4">No screening results yet. Click "Run New Screening".</div>;
          })()}

          {/* 6C: Screening History Timeline */}
          <h4 className="text-xs uppercase tracking-wider font-medium text-[#52525B] mb-2">Screening Timeline ({caseData.screening_history?.length || 0})</h4>
          {(!caseData.screening_history || caseData.screening_history.length === 0) ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA]">No screenings yet.</div>
          ) : (
            <div className="relative ml-4">
              {/* Timeline line */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-[#E4E4E7]" />
              {caseData.screening_history.map((s, i) => {
                const alertsForScreen = caseData.match_alerts?.filter(a => {
                  // Match alerts created around the same time as this screening
                  const sTime = new Date(s.screened_at).getTime();
                  const aTime = new Date(a.created_at).getTime();
                  return Math.abs(sTime - aTime) < 10000; // within 10s
                }) || [];
                const resolved = alertsForScreen.filter(a => a.status !== 'pending').length;
                const total = alertsForScreen.length;
                return (
                <div key={i} className="relative pl-6 pb-4">
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-1 w-2.5 h-2.5 rounded-full -translate-x-1/2 ${s.status === 'match' ? 'bg-red-500' : 'bg-green-500'}`} />
                  <div className="bg-white border border-[#E4E4E7] rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-[#0A0A0A]">{new Date(s.screened_at).toLocaleString()}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${s.status === 'match' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{s.status === 'match' ? `${s.total_matches} match(es)` : 'Clear'}</span>
                        {total > 0 && <span className="text-[10px] text-[#A1A1AA]">Alerts: {resolved}/{total} resolved</span>}
                      </div>
                      <span className="text-[10px] text-[#52525B]">by {s.screened_by}</span>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      )}

      {/* RISK TAB */}
      {tab === 'risk' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold">Risk Assessment</h3>
            <button onClick={handleRisk} disabled={riskCalc} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50">{riskCalc ? 'Calculating...' : 'Recalculate Risk'}</button>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Current Rating</p>
                <span className={`inline-block mt-1 px-3 py-1 rounded text-lg font-bold ${RISK_STYLES[caseData.risk_rating] || ''}`}>{caseData.risk_rating}</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Score</p>
                <p className="text-3xl font-bold mt-1">{caseData.risk_score}<span className="text-sm text-[#A1A1AA] font-normal">/100</span></p>
              </div>
              <div className="flex-1">
                <div className="w-full h-3 bg-[#F0F0F1] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${caseData.risk_score > 75 ? 'bg-red-500' : caseData.risk_score > 50 ? 'bg-orange-500' : caseData.risk_score > 25 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${caseData.risk_score}%` }} />
                </div>
              </div>
            </div>
          </div>
          {riskResult?.factors && (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
              <h4 className="text-sm font-semibold mb-3">Risk Factors</h4>
              <div className="space-y-2">
                {riskResult.factors.map((f, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0F0F1]">
                    <div><p className="text-sm font-medium">{f.factor}</p><p className="text-xs text-[#A1A1AA]">{f.detail}</p></div>
                    <span className={`text-sm font-bold ${f.contribution >= 20 ? 'text-red-600' : f.contribution >= 10 ? 'text-orange-600' : 'text-green-600'}`}>+{f.contribution}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MONITORING TAB */}
      {tab === 'monitoring' && (
        <div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-base font-semibold">Ongoing Monitoring</h3>
                <p className="text-xs text-[#52525B] mt-1">When enabled, this case is automatically re-screened whenever data sources sync.</p>
              </div>
              <button onClick={handleMonitoringToggle} className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${caseData.monitoring_enabled ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-[#F0F0F1] text-[#52525B] hover:bg-[#E4E4E7]'}`}>
                {caseData.monitoring_enabled ? 'Monitoring Active' : 'Enable Monitoring'}
              </button>
            </div>
            {caseData.last_monitored_at && <p className="text-xs text-[#A1A1AA] mt-2">Last monitored: {new Date(caseData.last_monitored_at).toLocaleString()}</p>}
          </div>

          {(() => {
            const hits = caseData.monitoring_hits || [];
            const pendingHits = hits.filter(h => h.status === 'pending');
            const allPendingHitsSelected = pendingHits.length > 0 && pendingHits.every(h => selectedHitIds.has(h.id));
            const toggleSelectAllHits = () => {
              if (allPendingHitsSelected) setSelectedHitIds(new Set());
              else setSelectedHitIds(new Set(pendingHits.map(h => h.id)));
            };
            return (
              <>
                <h4 className="font-heading text-sm font-semibold mb-3">Monitoring Hits ({hits.length})</h4>
                {pendingHits.length > 0 && (
                  <div className="flex items-center gap-2 mb-2 text-xs text-[#52525B]" data-testid="hit-bulk-select-all-row">
                    <input type="checkbox" checked={allPendingHitsSelected} onChange={toggleSelectAllHits} className="w-4 h-4 cursor-pointer accent-[#0052FF]" data-testid="hit-select-all" aria-label="Select all pending monitoring hits" />
                    <label className="cursor-pointer" onClick={toggleSelectAllHits}>{allPendingHitsSelected ? 'Deselect' : 'Select'} all {pendingHits.length} pending hit{pendingHits.length === 1 ? '' : 's'}</label>
                  </div>
                )}
                {hits.length === 0 ? (
                  <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA]">No monitoring hits detected.</div>
                ) : (
                  <div className="space-y-2">
                    {hits.map(h => {
                      const selectable = h.status === 'pending';
                      const checked = selectedHitIds.has(h.id);
                      return (
                      <div key={h.id} className={`bg-white border rounded-md p-4 ${h.status === 'pending' ? 'border-red-200' : 'border-[#E4E4E7]'} ${checked ? 'ring-2 ring-[#0052FF]/40' : ''}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            {selectable && (
                              <input type="checkbox" checked={checked} onChange={() => toggleHitSelect(h.id)} className="mt-1 w-4 h-4 cursor-pointer accent-[#0052FF]" data-testid={`hit-checkbox-${h.id}`} aria-label={`Select hit ${h.matched_entry_name}`} />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{h.matched_entry_name}</p>
                              <div className="flex gap-3 mt-1 text-xs text-[#52525B]">
                                <span className="uppercase">{h.hit_type}</span>
                                {h.matched_entry_source && <span>{h.matched_entry_source}</span>}
                                <span className="font-bold">{h.match_score?.toFixed(1)}% match</span>
                                <span>{new Date(h.detected_at).toLocaleString()}</span>
                                {h.source_url && (
                                  <a href={h.source_url} target="_blank" rel="noopener noreferrer" data-testid={`hit-article-link-${h.id}`} className="text-[#0052FF] hover:underline">Article link ↗</a>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-2 py-0.5 text-xs rounded font-medium ${h.status === 'pending' ? 'bg-red-50 text-red-700' : h.status === 'cleared' ? 'bg-green-50 text-green-700' : h.status === 'escalated' ? 'bg-orange-50 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>{h.status}</span>
                            {h.status === 'pending' && (
                              <div className="flex gap-1">
                                <button onClick={() => handleResolveHit(h.id, 'cleared')} className="px-2 py-0.5 text-[10px] bg-green-50 text-green-700 rounded hover:bg-green-100">Clear</button>
                                <button onClick={() => handleResolveHit(h.id, 'escalated')} className="px-2 py-0.5 text-[10px] bg-orange-50 text-orange-700 rounded hover:bg-orange-100">Escalate</button>
                                <button onClick={() => handleResolveHit(h.id, 'dismissed')} className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Dismiss</button>
                              </div>
                            )}
                          </div>
                        </div>
                        {h.resolved_by && <p className="text-[10px] text-[#A1A1AA] mt-2">Resolved by {h.resolved_by} at {new Date(h.resolved_at).toLocaleString()}</p>}
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}


      {/* MEDIA TAB */}
      {tab === 'media' && (
        <div data-testid="media-tab-panel">
          {caseData.media_alert_configured ? (
            <div>
              <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-base font-semibold flex items-center gap-2">📰 Media Monitoring — Active <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /></h3>
                    <p className="text-xs text-[#52525B] mt-1">Feed: {caseData.media_config?.feed_url?.substring(0, 60)}...</p>
                    {caseData.media_config?.last_checked && <p className="text-xs text-[#A1A1AA]">Last checked: {timeAgo(caseData.media_config.last_checked)}</p>}
                  </div>
                  <button onClick={async () => { setCheckingMedia(true); await api.post('/media-alerts/check-now').catch(() => {}); setTimeout(async () => { const r = await api.get(`/media-alerts/case/${id}`); setMediaAlerts(r.data.alerts || []); setCheckingMedia(false); }, 3000); }} disabled={checkingMedia} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50">{checkingMedia ? 'Checking...' : 'Check Now'}</button>
                </div>
              </div>

              {/* Filter sub-tabs */}
              {(() => {
                const counts = {
                  all: mediaAlerts.length,
                  pending: mediaAlerts.filter(a => a.status === 'pending').length,
                  flagged: mediaAlerts.filter(a => a.status === 'flagged').length,
                  dismissed: mediaAlerts.filter(a => a.status === 'dismissed').length,
                };
                const filters = [
                  { id: 'all', label: 'All', color: 'text-[#0A0A0A]' },
                  { id: 'pending', label: 'Pending', color: 'text-red-700' },
                  { id: 'flagged', label: 'Flagged', color: 'text-amber-700' },
                  { id: 'dismissed', label: 'Dismissed', color: 'text-gray-600' },
                ];
                return (
                  <div className="flex gap-1 mb-3 border-b border-[#E4E4E7]" data-testid="media-filter-tabs">
                    {filters.map(f => (
                      <button key={f.id} onClick={() => setMediaFilter(f.id)} data-testid={`media-filter-${f.id}`} className={`px-3 py-2 text-xs font-medium border-b-2 transition-all flex items-center gap-1.5 ${mediaFilter === f.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>
                        {f.label}
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${mediaFilter === f.id ? 'bg-[#0052FF]/10 text-[#0052FF]' : 'bg-[#F0F0F1] text-[#52525B]'}`}>{counts[f.id]}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* Article list (filtered by sub-tab) */}
              {(() => {
                const list = mediaFilter === 'all' ? mediaAlerts : mediaAlerts.filter(a => a.status === mediaFilter);
                if (list.length === 0) {
                  return <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA]">No {mediaFilter === 'all' ? '' : mediaFilter} articles.</div>;
                }
                const rowStyle = (s) => s === 'pending' ? 'border-red-200' : s === 'flagged' ? 'border-amber-200 bg-amber-50/30' : s === 'dismissed' ? 'border-[#E4E4E7] bg-[#F7F7F8]/50' : 'border-[#E4E4E7]';
                return (
                  <div className="space-y-2">
                    {list.map(a => (
                      <div key={a.id} data-testid={`media-alert-${a.id}`} className={`bg-white border rounded-md p-3 flex items-start justify-between gap-3 ${rowStyle(a.status)}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">📰 {a.title || 'Untitled'}</p>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#52525B]">
                            {a.source && <span>Source: <span className="font-medium text-[#0A0A0A]">{a.source}</span></span>}
                            {a.published_date && <span>{a.published_date}</span>}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${a.status === 'pending' ? 'bg-red-50 text-red-700' : a.status === 'flagged' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                            {a.resolved_by && a.status !== 'pending' && <span className="text-[#A1A1AA]">by {a.resolved_by}</span>}
                          </div>
                          {a.summary && <p className="text-xs text-[#52525B] mt-1 line-clamp-2">{a.summary}</p>}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" data-testid={`read-${a.id}`} className="px-2.5 py-1 text-xs border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] flex items-center gap-1"><ArrowSquareOut size={12} /> Read</a>}
                          {a.status === 'pending' && (
                            <>
                              <button onClick={() => setResModal({ open: true, type: 'media', id: a.id, resolution: 'dismissed', matchName: a.title })} className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Dismiss</button>
                              <button onClick={() => setResModal({ open: true, type: 'media', id: a.id, resolution: 'flagged', matchName: a.title })} className="px-2.5 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100">Flag</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {mediaAlerts.length === 0 && <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA] mt-3">No articles found yet. Feed will be checked during daily sync.</div>}
            </div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-6">
              <h3 className="font-heading text-base font-semibold mb-2">📰 Media Monitoring</h3>
              <p className="text-sm text-[#52525B] mb-4">No media alert configured for this case. <HelpTooltip text="Google Alerts monitors the web for mentions of this case name. When articles are found, they appear here for review. Google does the searching — Radar collects the results." /></p>
              <button onClick={() => window.open(`https://www.google.com/alerts#create:q=${encodeURIComponent(caseData.full_name)}&d=rss`, '_blank')} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] mb-4">Set Up Google Alert →</button>
              <div className="bg-[#F7F7F8] rounded-md p-4 text-xs text-[#52525B] mb-4">
                <p className="font-medium mb-1">How it works:</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Click the button above — Google Alerts opens with "{caseData.full_name}" pre-filled</li>
                  <li>On Google Alerts, create the alert and select "RSS feed" as delivery</li>
                  <li>Copy the RSS feed URL and paste it below</li>
                </ol>
              </div>
              <div className="flex gap-2">
                <input value={mediaFeedUrl} onChange={e => setMediaFeedUrl(e.target.value)} placeholder="Paste RSS feed URL here..." className="flex-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md font-mono text-xs" />
                <button onClick={async () => {
                  if (!mediaFeedUrl.trim()) return;
                  setSavingFeed(true);
                  try { await api.post('/media-alerts/config', { case_id: id, feed_url: mediaFeedUrl }); setMediaFeedUrl(''); await fetchCase(); } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
                  setSavingFeed(false);
                }} disabled={savingFeed || !mediaFeedUrl.trim()} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">{savingFeed ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AUDIT TAB */}
      {tab === 'audit' && (
        <div>
          <h3 className="font-heading text-base font-semibold mb-3">Case Audit Trail</h3>
          {audits.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-6 text-center text-sm text-[#A1A1AA]">No audit entries.</div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
              {audits.map((a, i) => (
                <div key={i} className="px-4 py-3 border-b border-[#E4E4E7] last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-[#F0F0F1] font-mono">{a.action}</span>
                      <span className="text-xs text-[#52525B]">by {a.user_email}</span>
                    </div>
                    <span className="text-xs text-[#A1A1AA]">{new Date(a.timestamp).toLocaleString()}</span>
                  </div>
                  {(a.before || a.after) && (
                    <div className="mt-2">
                      <table className="text-xs w-full max-w-md">
                        <thead><tr className="border-b border-[#E4E4E7]"><th className="text-left py-1 pr-3 text-[#52525B] font-medium">Field</th><th className="text-left py-1 pr-3 text-red-600 font-medium">Before</th><th className="text-left py-1 text-green-600 font-medium">After</th></tr></thead>
                        <tbody>{Object.keys({...(a.before||{}), ...(a.after||{})}).map(k => (
                          <tr key={k} className="border-b border-[#F0F0F1]"><td className="py-1 pr-3 text-[#52525B]">{formatFieldName(k)}</td><td className="py-1 pr-3 text-red-600">{a.before?.[k] ? formatLabel(String(a.before[k])) : '—'}</td><td className="py-1 text-green-600">{a.after?.[k] ? formatLabel(String(a.after[k])) : '—'}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  {a.details && Object.keys(a.details).length > 0 && !a.before && !a.after && (
                    <p className="text-xs text-[#A1A1AA] mt-1">{JSON.stringify(a.details)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Resolution Modal */}
      <ResolutionModal isOpen={resModal.open} resolution={resModal.resolution} matchName={resModal.matchName} onConfirm={confirmResolution} onCancel={() => setResModal({ open: false, type: null, id: null, resolution: null, matchName: null, matchData: null })} caseData={caseData} matchData={resModal.matchData} />

      {/* Bulk Action Bar — match alerts (Screening tab). Reuses same PUT /match-alerts/{id}/resolve as single-row buttons. */}
      <BulkActionBar
        selectedCount={selectedAlertIds.size}
        onAction={(action, comment) => runBulkMatchAlertsAction(action, comment)}
        onClear={clearAlertSelection}
        actions={[
          { key: 'true_positive',  label: 'True Positive',  className: 'bg-red-50 text-red-700 hover:bg-red-100' },
          { key: 'false_positive', label: 'False Positive', className: 'bg-green-50 text-green-700 hover:bg-green-100' },
          { key: 'inconclusive',   label: 'Inconclusive',   className: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
          { key: 'whitelisted',    label: 'Whitelist',      className: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
        ]}
      />

      {/* Bulk Action Bar — monitoring hits (Monitoring tab). Reuses same PUT /monitoring/hits/{id}/resolve as single-row buttons. */}
      <BulkActionBar
        selectedCount={selectedHitIds.size}
        onAction={(action, comment) => runBulkMonitoringHitsAction(action, comment)}
        onClear={clearHitSelection}
        actions={[
          { key: 'cleared',   label: 'Clear',    className: 'bg-green-50 text-green-700 hover:bg-green-100' },
          { key: 'escalated', label: 'Escalate', className: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
          { key: 'dismissed', label: 'Dismiss',  className: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
        ]}
      />

      {/* Match Detail (read-only) Dialog */}
      <MatchDetailDialog open={matchDetail.open} alert={matchDetail.alert} caseData={caseData} onClose={() => setMatchDetail({ open: false, alert: null })} />

      {/* Case Report PDF Preview */}
      <ReportPreviewModal
        open={reportPreviewOpen}
        path={`/reports/case/${id}/pdf`}
        title={`Case Report — ${caseData.full_name}`}
        filename={`Radar_${(caseData.full_name||'Case').replace(/ /g,'_')}.pdf`}
        onClose={() => setReportPreviewOpen(false)}
      />
    </div>
  );
}


// ─── Reusable Match Alert Card Component ─────────────────────────
function MatchAlertCard({ alert: a, caseData, onResolve }) {
  const ed = a.matched_entry_data || {};
  const matchScore = typeof a.match_score === 'number' ? a.match_score : 0;
  const matchLayers = a.match_layers && typeof a.match_layers === 'object' ? a.match_layers : {};
  const aliases = Array.isArray(ed.aliases) ? ed.aliases : [];
  const tierColor = matchScore >= 85 ? 'border-l-4 border-l-red-500' : matchScore >= 60 ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-gray-300';
  try { return (
    <div className={`bg-white border border-[#E4E4E7] rounded-md overflow-hidden ${tierColor}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-[#F7F7F8] border-b border-[#E4E4E7]">
        <div className="flex items-center gap-2">
          <span className={ALERT_STATUS_STYLES[a.status] || BADGE}>{formatLabel(a.status || 'unknown')}</span>
          <span className={`text-sm font-bold ${matchScore >= 85 ? 'text-red-600' : matchScore >= 60 ? 'text-orange-600' : 'text-gray-600'}`}>{matchScore.toFixed(1)}%</span>
          <span className="text-xs text-[#A1A1AA] uppercase">{a.database || 'N/A'}</span>
          <span className={`${BADGE} text-xs ${matchScore >= 85 ? 'bg-red-100 text-red-700 border-red-200' : matchScore >= 60 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{matchScore >= 85 ? 'Strong' : matchScore >= 60 ? 'Potential' : 'Weak'}</span>
        </div>
        {a.resolved_by && <span className="text-xs text-[#A1A1AA]">Resolved by {a.resolved_by}</span>}
      </div>
      <div className="grid grid-cols-2 divide-x divide-[#E4E4E7]">
        <div className="p-4">
          <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Case Entity</p>
          <p className="text-sm font-semibold">{caseData.full_name || 'N/A'}</p>
          <div className="space-y-1 mt-2 text-xs text-[#52525B]">
            {caseData.date_of_birth && <p>DOB: {caseData.date_of_birth}</p>}
            {caseData.nationality && <p>Nationality: {caseData.nationality}</p>}
            {caseData.id_type && <p>ID: {caseData.id_type} {caseData.id_number || ''}</p>}
          </div>
        </div>
        <div className="p-4">
          <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Matched Record</p>
          <p className="text-sm font-semibold">{a.matched_entry_name || 'N/A'}</p>
          <div className="space-y-1 mt-2 text-xs text-[#52525B]">
            {aliases.length > 0 && <p><strong>Aliases:</strong> {aliases.slice(0,5).join(', ')}</p>}
            {ed.date_of_birth && <p><strong>DOB:</strong> {ed.date_of_birth}</p>}
            {ed.nationality && <p><strong>Nationality:</strong> {ed.nationality}</p>}
            {ed.program && <p><strong>Programme:</strong> {ed.program}</p>}
            {ed.reason && <p><strong>Reason:</strong> {String(ed.reason).slice(0,200)}</p>}
            {ed.designation_date && <p><strong>Designated:</strong> {ed.designation_date}</p>}
            {a.matched_entry_source && <p><strong>Source:</strong> {a.matched_entry_source}</p>}
            {(a.source_url || ed.source_url) && <a href={a.source_url || ed.source_url} target="_blank" rel="noopener noreferrer" data-testid={`alert-article-link-${a.id}`} className="text-[#0052FF] hover:underline text-xs">Article link ↗</a>}
          </div>
          {Object.keys(matchLayers).length > 0 && (
            <div className="mt-2 pt-2 border-t border-[#F0F0F1]">
              <div className="flex gap-1.5 flex-wrap">
                {Object.entries(matchLayers).map(([k,v]) => <span key={k} className={`${BADGE} text-xs ${typeof v==='number' && v>50 ? 'bg-red-50 text-red-700 border-red-200' : typeof v==='number' && v>0 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>{k}: {typeof v==='number' ? v.toFixed(0) : 0}%</span>)}
              </div>
            </div>
          )}
        </div>
      </div>
      {a.status === 'pending' && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[#F7F7F8] border-t border-[#E4E4E7]">
          <button onClick={() => onResolve(a.id, 'true_positive')} className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700">True Positive</button>
          <button onClick={() => onResolve(a.id, 'false_positive')} className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700">False Positive</button>
          <button onClick={() => onResolve(a.id, 'inconclusive')} className="px-3 py-1 text-xs font-medium bg-gray-500 text-white rounded hover:bg-gray-600">Inconclusive</button>
          <button onClick={() => onResolve(a.id, 'whitelisted')} className="px-3 py-1 text-xs font-medium border border-blue-300 text-blue-700 rounded hover:bg-blue-50">Whitelist</button>
        </div>
      )}
    </div>
  ); } catch (err) { return <div className="border border-[#E4E4E7] rounded-md p-3 text-xs text-red-600">Render error: {err.message}</div>; }
}

// ─── Weak Matches Collapsible Section ────────────────────────────
function WeakMatchesSection({ alerts, caseData, onResolve, onView, selectedIds, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  if (!alerts.length) return null;
  const hasSelection = selectedIds && onToggleSelect;
  return (
    <div className="mb-4">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 py-2 border-b-2 border-gray-300 text-left">
        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">{expanded ? '▼' : '▶'} Weak Matches ({alerts.length}) — Score 40-59% (click a row to view details)</span>
      </button>
      {expanded && (
        <div className="mt-2 bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
              {hasSelection && <th className="px-3 py-2 w-8"></th>}
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Score</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Matched Name</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Source</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Nationality</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Programme</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-[#52525B]">Actions</th>
            </tr></thead>
            <tbody>
              {alerts.map(a => (
                <tr key={a.id} className={`border-b border-[#F0F0F1] hover:bg-[#F7F7F8] cursor-pointer ${hasSelection && selectedIds.has(a.id) ? 'bg-[#0052FF]/5' : ''}`} onClick={() => onView && onView(a)} data-testid={`weak-match-row-${a.id}`}>
                  {hasSelection && (
                    <td className="px-3 py-2 w-8" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(a.id)} onChange={() => onToggleSelect(a.id)} className="w-4 h-4 cursor-pointer accent-[#0052FF]" data-testid={`alert-checkbox-${a.id}`} aria-label={`Select alert ${a.matched_entry_name}`} />
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs font-bold text-gray-600">{a.match_score?.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-xs font-medium">{a.matched_entry_name || 'N/A'}</td>
                  <td className="px-3 py-2 text-xs">{a.matched_entry_source || a.database || ''}</td>
                  <td className="px-3 py-2 text-xs">{a.matched_entry_data?.nationality || ''}</td>
                  <td className="px-3 py-2 text-xs text-[#52525B]">{a.matched_entry_data?.program || ''}</td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => onResolve(a.id, 'false_positive')} className="px-1.5 py-0.5 text-[10px] bg-green-50 text-green-700 rounded hover:bg-green-100" title="False Positive">✓ FP</button>
                      <button onClick={() => onResolve(a.id, 'whitelisted')} className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded hover:bg-blue-100" title="Whitelist">★ WL</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
